/**
 * The Business Units add / edit form, as a RIGHT-SIDE PANEL.
 *
 * Owner ruling, 2026-10-05 (DECISIONS.md D-017): *"Let's get rid of pop-up window deesign. Instead
 * of 'Add Business Unit' window, I want you to follow up and open the panel on the right side with
 * those fields and follow the UI design for that right panel … I mean SAve and cance button (Save
 * should be icon Save) etc."* It replaced the centred MUI `Dialog` this screen used to open.
 *
 * ── FOUR FIELDS NOW, WHERE THERE WERE EIGHT — D-022, migration `0103` ──────────────────
 *
 * The owner: *"what is the purpose of the Code and Order fields on Business Unit definitin? also
 * what does it mean Parent? There should be no Parent for Business Unit. Your design went way too
 * dep - a Business Unit (BU) is a BU"* and *"I do not need Code not Order. for that prupose we could
 * use Region or Country. Also remove Parent and Active flag. All Business Units will be only active
 * if they are defined"*.
 *
 * So **Name, Description, Region, Country**, and nothing else. Gone with the columns: the Code
 * field, the Order field, the Parent picker (`SelectField`, which is why the ui-kit is no longer
 * imported here) and the Active checkbox (which is why this file no longer has a stylesheet — the
 * checkbox was its only rule). The server's cycle refusal and its "no such parent" refusal are gone
 * with the picker; `name` is required and that is the only client-side rule left.
 *
 * `../chrome/FormPanel.tsx` carries the panel chrome and cites every measurement to
 * `pages/master-data/ApplicationDetailDrawer.tsx` by way of `initiatives/InitiativeDetailPanel.css`
 * — the right anchor, the identity header with its red close control, the scrolling body, and the
 * footer whose Save carries the Save icon with Cancel quiet beside it. Cloned, never imported.
 *
 * SINGLE WIDTH, which is the default (`../chrome/panelWidths.css`). Four fields fit it easily;
 * nothing here is a side-by-side comparison or a wide table.
 */
import { useEffect, useState } from 'react';
import { FormPanel } from '../chrome/FormPanel';
import { erpApi } from '../api';
import { type BusinessUnit, type BusinessUnitWrite } from '../types';

/** The four fields, as strings — the form holds text, the save coerces. */
interface FormState {
  name: string;
  description: string;
  country: string;
  region: string;
}

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  country: '',
  region: '',
});

const formFrom = (unit: BusinessUnit): FormState => ({
  name: unit.name,
  description: unit.description ?? '',
  country: unit.country ?? '',
  region: unit.region ?? '',
});

export interface BusinessUnitDetailPanelProps {
  open: boolean;
  /** `null` adds a unit; a unit edits that one. */
  unit: BusinessUnit | null;
  onClose: () => void;
  /** Called after a successful write, with the sentence the screen should show. */
  onSaved: (message: string) => void;
}

export function BusinessUnitDetailPanel({
  open,
  unit,
  onClose,
  onSaved,
}: BusinessUnitDetailPanelProps) {
  const [form, setForm] = useState<FormState>(() => emptyForm());
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Seeded when the panel OPENS, and on the record it opens on — not on every render of the unit
   * list behind it. `ApplicationDetailDrawer.tsx:562-586` records what re-seeding on a background
   * reload costs: an edit in progress is wiped, and it reads as data loss.
   */
  useEffect(() => {
    if (!open) return;
    setForm(unit ? formFrom(unit) : emptyForm());
    setDirty(false);
    setError(null);
    // The identity of the record is what matters, not the object. See above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, unit?.id]);

  /** Any keystroke marks the form dirty and clears a stale refusal. */
  const change = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setDirty(true);
    setError(null);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setBusy(true);
    setError(null);
    const body: BusinessUnitWrite = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      country: form.country.trim() || null,
      region: form.region.trim() || null,
    };
    try {
      if (unit) {
        await erpApi.businessUnits.patch(unit.id, body);
        setDirty(false);
        onSaved('Business unit updated.');
      } else {
        await erpApi.businessUnits.create(body);
        setDirty(false);
        onSaved('Business unit added.');
      }
    } catch (err) {
      /* A server refusal arrives display-ready and is shown in the panel, not in the strip behind
         it, which the old dialog covered up. */
      setError(err instanceof Error ? err.message : 'Could not save this business unit');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <FormPanel
      open
      title={unit ? unit.name : 'Add Business Unit'}
      /* The identity line, as the precedent has it: a second line under the name. A unit's code
         used to sit here and there is no code any more (D-022), so an existing unit shows the two
         facts that now place it — its region and country — and a unit being added says what the
         panel is for. */
      subtitle={
        unit
          ? [unit.region, unit.country].filter((value) => value).join(' · ') ||
            'No region or country'
          : 'New business unit'
      }
      onRequestClose={onClose}
      onSave={() => void save()}
      busy={busy}
      dirty={dirty}
      discardMessage="Discard your changes to this business unit?"
    >
      {error !== null && <div className="erp-panel-error">⚠️ {error}</div>}

      {/* FOUR FIELDS: Name, Region, Country, Description. `.form-group` and `.form-row` are the
          module's shared form vocabulary (`../source-vocabulary.css`, cited there to
          `app.css:3359-3405`). Region and Country keep the paired row they were already in. */}
      <div className="form-group">
        <label htmlFor="erp-bu-name">Name *</label>
        <input
          id="erp-bu-name"
          type="text"
          required
          value={form.name}
          onChange={(e) => change('name', e.target.value)}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="erp-bu-region">Region</label>
          <input
            id="erp-bu-region"
            type="text"
            value={form.region}
            onChange={(e) => change('region', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="erp-bu-country">Country</label>
          <input
            id="erp-bu-country"
            type="text"
            value={form.country}
            onChange={(e) => change('country', e.target.value)}
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="erp-bu-description">Description</label>
        <textarea
          id="erp-bu-description"
          rows={2}
          value={form.description}
          onChange={(e) => change('description', e.target.value)}
        />
      </div>
    </FormPanel>
  );
}
